/**
 * The client's copy of her nightly-generated personal lines, and the id
 * history that keeps one of them from repeating too soon.
 *
 * The server owns generation (`glitime-lines.ts`'s nightly batches, built
 * from this account's own memories and transcripts); this module caches the
 * active set per account in Dexie meta so a greeting, a presence remark or a
 * log reaction can reach for a personal callback with the phone in airplane
 * mode. The cache refreshes on boot and after every chat close, mirroring
 * `promises.ts` exactly — a fresh batch or an admin deletion propagates on
 * the same two occasions a promise would.
 *
 * These lines are deliberately kept OUT of `sass/engine.ts`'s catalogue
 * pools: `resolvePool`'s repeat-history is keyed by array index within a
 * pool, and a personalised batch can change overnight — if it joined a
 * catalogue pool, an overnight regeneration would silently shift what index
 * N means and corrupt the no-repeat memory. So a personal line gets its own
 * id-keyed draw (`takePersonalLine`), entirely separate from `pickIndex`,
 * layered on top of the catalogue's pick by the engine rather than inside it.
 *
 * Selection here is deliberately dumb, same posture as `matchPromise`: an
 * exact surface key, or a shared food class, or a folded-substring look at
 * the subject. Anything cleverer would need to be right about Lithuanian
 * morphology to be worth it.
 */

import { foldLt } from '@gramas/shared'
import { api } from '../api/client.js'
import { getMeta, setMeta } from '../db/dexie.js'
import { register } from '../sass/engine.js'
import { currentProfile } from '../screens/useProfile.js'

export interface CachedLine {
  id: string
  surface: string
  title: string | null
  text: string
  foodClass: string | null
  subject: string | null
  createdAt: string
  expiresAt: string
}

const ANON_OWNER = 'anon'

function ownerKey(): string {
  return currentProfile()?.id ?? ANON_OWNER
}

function linesMetaKey(owner: string): string {
  return `glitime.lines.${owner}`
}
/** Last-shown timestamp per line id — a second, independent Dexie store, so
 * `resolvePool`'s own history never has to know a personal line exists. */
function personalMetaKey(owner: string): string {
  return `sass.personal.${owner}`
}

const cache = new Map<string, CachedLine[]>()
const personalHistory = new Map<string, Record<string, number>>()
const hydrationPromises = new Map<string, Promise<void>>()

function hydrate(owner: string): Promise<void> {
  let promise = hydrationPromises.get(owner)
  if (promise == null) {
    promise = Promise.all([
      getMeta<CachedLine[]>(linesMetaKey(owner), []),
      getMeta<Record<string, number>>(personalMetaKey(owner), {}),
    ]).then(([lines, history]) => {
      // A write already landed while this was in flight — that write is the
      // truth now, adopting the read underneath it would throw a live pick away.
      if (!cache.has(owner)) cache.set(owner, lines)
      if (!personalHistory.has(owner)) personalHistory.set(owner, history)
    })
    hydrationPromises.set(owner, promise)
  }
  return promise
}

/** Pull the server's current active set into the cache. Fire-and-forget friendly. */
export async function refreshLines(): Promise<void> {
  const owner = ownerKey()
  if (owner === ANON_OWNER) return
  try {
    const wire = await api.get<{ lines: CachedLine[] }>('/api/glitime/lines')
    cache.set(owner, wire.lines)
    await setMeta(linesMetaKey(owner), wire.lines)
    // Ids the server no longer lists (expired, deleted, source memory gone)
    // cannot be shown again, so their shown-at stamp is dead weight.
    const live = new Set(wire.lines.map((line) => line.id))
    const history = personalHistory.get(owner) ?? {}
    const kept: Record<string, number> = {}
    for (const [id, at] of Object.entries(history)) if (live.has(id)) kept[id] = at
    personalHistory.set(owner, kept)
    await setMeta(personalMetaKey(owner), kept)
  } catch {
    // Offline or signed out: the cached copy keeps working, which is the point.
  }
}

/** This account's cached lines, live ones only — hydrates on first touch. */
function liveCandidates(owner: string): CachedLine[] {
  void hydrate(owner)
  const now = Date.now()
  return (cache.get(owner) ?? []).filter((line) => new Date(line.expiresAt).getTime() > now)
}

/**
 * All cached, still-live lines for one surface — a greeting or a presence
 * remark reaches for these directly. `[]` for the dry register: the
 * generator only ever writes sassy-register lines, so a dry account must
 * never see one drawn out of the cache.
 */
export function personalFor(surface: string): CachedLine[] {
  if (register() !== 'sassy') return []
  return liveCandidates(ownerKey()).filter((line) => line.surface === surface)
}

/**
 * Live `log_reaction` lines that speak to what was just logged — a shared
 * food class where both sides have one, else a folded-substring look at the
 * line's subject, same trick `matchPromise` uses for promises.
 */
export function personalLogReactions(
  itemName: string | undefined,
  foodClass: string | undefined,
): CachedLine[] {
  if (register() !== 'sassy') return []
  const folded = itemName ? foldLt(itemName) : null
  return liveCandidates(ownerKey()).filter((line) => {
    if (line.surface !== 'log_reaction') return false
    if (line.foodClass != null && foodClass != null && line.foodClass === foodClass) return true
    if (folded != null && line.subject != null && line.subject.length >= 4) {
      return folded.includes(line.subject) || line.subject.includes(folded)
    }
    return false
  })
}

/**
 * Draw one line out of `candidates` that hasn't been shown within
 * `repeatWindowMs`, or null when every candidate is still quarantined (or
 * there were none to begin with). Stamps the pick's shown-at time on the way
 * out — synchronously in the cache, fire-and-forget to Dexie, matching every
 * other write in this file and in `sass/engine.ts`: a line pick never waits
 * on a database round-trip.
 */
export function takePersonalLine(
  candidates: CachedLine[],
  repeatWindowMs: number,
  rng: () => number,
): CachedLine | null {
  if (candidates.length === 0) return null
  const owner = ownerKey()
  void hydrate(owner)
  const history = personalHistory.get(owner) ?? {}
  const now = Date.now()
  const eligible = candidates.filter((line) => {
    const lastShown = history[line.id]
    return lastShown == null || now - lastShown >= repeatWindowMs
  })
  if (eligible.length === 0) return null

  const pick = eligible[Math.floor(rng() * eligible.length) % eligible.length]!
  const next = { ...history, [pick.id]: now }
  personalHistory.set(owner, next)
  void setMeta(personalMetaKey(owner), next)
  return pick
}

/**
 * A specific line id, if it is still active and cached — for resurrecting a
 * `dailyLine` pin across two reads of the same date. Null means the line was
 * deleted or expired since it was pinned, which the caller falls through on
 * rather than treating as an error.
 */
export function resolvePersonalPin(lineId: string): CachedLine | null {
  return liveCandidates(ownerKey()).find((line) => line.id === lineId) ?? null
}

/** Test-only: wait for an account's Dexie-backed line/history cache to load. */
export function __hydrateLinesForTests(owner: string): Promise<void> {
  return hydrate(owner)
}
/** Test-only: drop the in-memory caches so the next read starts cold. */
export function __resetLinesForTests(): void {
  cache.clear()
  personalHistory.clear()
  hydrationPromises.clear()
}
